<template>
  <div v-if="isLoading" class="flex items-center justify-center px-16">
    <JIcon name="spinner" size="40" />
  </div>

  <MyTravelPreferencesFormFields
    v-else
    :travelPreferences="publicTravelPreferences"
    :userTravelPreferences="userTravelPreferences"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { JIcon } from '@journey/ui-kit'
import MyTravelPreferencesFormFields from './MyTravelPreferencesFormFields/MyTravelPreferencesFormFields.vue'
import { MINIMUM_NUMBER_OF_SEARCH_RESULTS } from '@/modules/Profile/constants'
import type { UserTravelPreferences } from '@/modules/Profile/types/model/travelPreferences'
import {
  useTravelPreferencesPublicQuery,
  useTravelPreferencesQuery,
} from '@/modules/Profile/requests/queries'
import featureFlag from '@/config/featureFlag'

// FETCH DATA
const { data: userData, isLoading: isLoadingUserData } =
  useTravelPreferencesQuery()
const { data: publicData, isLoading: isLoadingPublicData } =
  useTravelPreferencesPublicQuery()

const publicTravelPreferences = computed(() => {
  const data = publicData.value?.data?.travelPrefs
  if (!data) {
    return {}
  }

  const { car, flight, hotel } = featureFlag.activeResults
  const enabledGroups = {
    'Car preferences': car,
    'Flight preferences': flight,
    'Hotel preferences': hotel,
  }

  return Object.fromEntries(
    Object.entries(data).filter(
      ([group]) => enabledGroups[group as keyof typeof enabledGroups] !== false
    )
  )
})

const userTravelPreferences = computed(() => {
  if (!userData.value?.data) {
    // TODO: This is the default data that should be selected for each new user
    //  that hasn't saved his/her travel preferences. It's ideal to be returned
    //  by the API.
    return {
      numberOfSearchResult: MINIMUM_NUMBER_OF_SEARCH_RESULTS.toString(),
    } satisfies UserTravelPreferences
  }

  const localUserTravelPreferences: UserTravelPreferences = {}

  Object.keys(publicTravelPreferences.value).forEach((groupName: string) => {
    publicTravelPreferences.value[groupName].forEach(
      (_: unknown, index: number) => {
        localUserTravelPreferences[
          publicTravelPreferences.value[groupName][index].type
        ] =
          userData.value?.data[
            publicTravelPreferences.value[groupName][index].type
          ]
      }
    )
  })

  const { nonPreferredConnectionPoints, preferredConnectionPoints } =
    userData.value.data

  localUserTravelPreferences.nonPreferredConnectionPoints =
    nonPreferredConnectionPoints?.map(
      ({
        geoCity,
        geoContinent,
        geoCountry,
        geoLocation,
        locationType,
        geoAreaState,
      }) => ({
        geoAreaState,
        geoCity,
        geoContinent,
        geoCountry,
        geoLocation,
        locationType,
      })
    )
  localUserTravelPreferences.preferredConnectionPoints =
    preferredConnectionPoints?.map(
      ({
        geoCity,
        geoContinent,
        geoCountry,
        geoLocation,
        locationType,
        geoAreaState,
      }) => ({
        geoAreaState,
        geoCity,
        geoContinent,
        geoCountry,
        geoLocation,
        locationType,
      })
    )

  return localUserTravelPreferences
})

// LOADING
const isLoading = computed(
  () => isLoadingUserData.value || isLoadingPublicData.value
)
</script>
